import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageAlert from "../../components/PageAlert/PageAlert";
import BlockToolbar from "./components/BlockToolbar";
import { getApiErrorMessage, handleApiError, resolveApiError } from "../../utils/apiError";
import BlockCanvas from "./components/BlockCanvas";
import BlockInspector from "./components/BlockInspector";
import {
  createArticle,
  createClientBlock,
  getArticle,
  getArticleBlocks,
  assignedArticleCategories,
  getArticleCategories,
  getArticleCategoryId,
  getArticleSubcategoryId,
  getFeaturedMediaId,
  getFeaturedMediaUrl,
  makeSlug,
  mergeArticleCategoryLists,
  normalizeArticleBlock,
  normalizePaginated,
  saveArticleBlocks,
  updateArticle,
} from "./articleApi";
import "./articles.scss";

const getUuid = () =>
  window.crypto?.randomUUID?.() || `block-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createBlock = (type) => createClientBlock(type);

const mapApiBlock = (block) => {
  const normalized = normalizeArticleBlock(block);
  return {
    id: block.id ? `db-${block.id}` : getUuid(),
    database_id: block.id || undefined,
    type: normalized.type,
    data: normalized.data || {},
    sort_order: block.sort_order,
    status: block.status,
  };
};

const mapApiBlocksUnique = (items = []) => {
  const seen = new Set();

  return items
    .map(mapApiBlock)
    .filter((block) => {
      if (block.type === "gallery") return false;
      if (!block.database_id) return true;
      const key = String(block.database_id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const FORM_SNAPSHOT_KEYS = [
  "title",
  "slug",
  "excerpt",
  "content",
  "status",
  "visibility",
  "featured_image",
  "featured_media_id",
  "meta_title",
  "meta_description",
  "meta_keyword",
  "category_id",
  "subcategory_id",
];

const snapshotForm = (form) => {
  const result = {};
  FORM_SNAPSHOT_KEYS.forEach((key) => {
    result[key] = form[key] ?? (key.endsWith("_ids") ? [] : "");
  });
  return result;
};

const snapshotBlocks = (blockList) =>
  blockList.map(({ type, data, database_id }) => ({
    type,
    data,
    database_id: database_id ?? null,
  }));

const buildSnapshot = (formState, blockState) => ({
  form: snapshotForm(formState),
  blocks: snapshotBlocks(blockState),
});

const snapshotsEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const savedRecordId = (record) => record?.id ?? record?.article?.id ?? null;

const parseBlockValidationErrors = (errors = {}, blocks = []) => {
  const byIndex = new Map();

  Object.entries(errors || {}).forEach(([key, messages]) => {
    const match = String(key).match(/^blocks\.(\d+)(?:\.|$)/);
    if (!match) return;

    const index = Number(match[1]);
    const list = (Array.isArray(messages) ? messages : [messages]).filter(Boolean);
    if (!list.length) return;

    byIndex.set(index, [...(byIndex.get(index) || []), ...list]);
  });

  return [...byIndex.entries()]
    .map(([index, messages]) => {
      const block = blocks[index];
      if (!block) return null;
      return {
        blockId: block.id,
        index,
        message: messages.join(" "),
      };
    })
    .filter(Boolean);
};

const scrollToBlock = (blockId) => {
  window.requestAnimationFrame(() => {
    const node = document.querySelector(`[data-block-id="${CSS.escape(String(blockId))}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};

const ArticleEditor = () => {
  const navigate = useNavigate();
  const params = useParams();
  const routeArticleId = params.id;
  const isRouteEdit = Boolean(routeArticleId);

  const [persistedArticleId, setPersistedArticleId] = useState(routeArticleId || null);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    status: "draft",
    visibility: "public",
    featured_image: "",
    featured_media_id: "",
    meta_title: "",
    meta_description: "",
    meta_keyword: "",
    category_id: "",
    subcategory_id: "",
  });
  const [blocks, setBlocks] = useState([createBlock("paragraph")]);
  const [categories, setCategories] = useState([]);
  const [assignedCategories, setAssignedCategories] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [blockErrors, setBlockErrors] = useState({});
  const [loading, setLoading] = useState(isRouteEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [showPreviewWarning, setShowPreviewWarning] = useState(false);

  const articleId = persistedArticleId || routeArticleId || null;
  const isEdit = Boolean(articleId);

  useEffect(() => {
    setPersistedArticleId(routeArticleId || null);
  }, [routeArticleId]);

  const taxonomyCategories = useMemo(
    () => mergeArticleCategoryLists(categories, assignedCategories),
    [categories, assignedCategories]
  );

  const isDirty = useMemo(() => {
    if (!isEdit || loading || !savedSnapshot) return false;
    return !snapshotsEqual(buildSnapshot(form, blocks), savedSnapshot);
  }, [form, blocks, savedSnapshot, isEdit, loading]);

  const updateForm = (key, value) => {
    setForm((prev) => {
      const patch = typeof key === "object" && value === undefined ? key : { [key]: value };
      const next = { ...prev, ...patch };
      if ("title" in patch && !isEdit) {
        next.slug = makeSlug(next.title);
      }
      return next;
    });
  };

  const clearBlockError = (blockId) => {
    setBlockErrors((prev) => {
      if (!prev[blockId]) return prev;
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
  };

  const addBlock = (type, afterIndex = null) => {
    const newBlock = createBlock(type);
    setBlocks((prev) => {
      if (afterIndex === null) return [...prev, newBlock];
      const next = [...prev];
      next.splice(afterIndex + 1, 0, newBlock);
      return next;
    });
    setSelectedBlockId(newBlock.id);
  };

  const updateBlock = (blockId, nextData) => {
    clearBlockError(blockId);
    setBlocks((prev) =>
      prev.map((block) =>
        block.id === blockId ? { ...block, data: { ...block.data, ...nextData } } : block
      )
    );
  };

  const updateBlockType = (blockId, type) => {
    clearBlockError(blockId);
    setBlocks((prev) =>
      prev.map((block) =>
        block.id === blockId
          ? { ...createBlock(type), id: block.id, database_id: block.database_id }
          : block
      )
    );
  };

  const removeBlock = (blockId) => {
    clearBlockError(blockId);
    setBlocks((prev) => {
      const next = prev.filter((block) => block.id !== blockId);
      setSelectedBlockId((current) => (current === blockId ? next[0]?.id || null : current));
      return next;
    });
  };

  const duplicateBlock = (blockId) => {
    setBlocks((prev) => {
      const index = prev.findIndex((block) => block.id === blockId);
      if (index === -1) return prev;
      const copy = {
        ...prev[index],
        id: getUuid(),
        database_id: undefined,
        data: JSON.parse(JSON.stringify(prev[index].data || {})),
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      setSelectedBlockId(copy.id);
      return next;
    });
  };

  const moveBlock = (blockId, direction) => {
    setBlocks((prev) => {
      const index = prev.findIndex((block) => block.id === blockId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const loadTaxonomies = useCallback(async () => {
    try {
      const categoryResponse = await getArticleCategories({ per_page: 200 });
      setCategories(normalizePaginated(categoryResponse).items);
    } catch (err) {
      // Taxonomy panels can remain empty if loading fails.
    }
  }, []);

  useEffect(() => {
    loadTaxonomies();
  }, [loadTaxonomies]);

  useEffect(() => {
    if (!isRouteEdit) {
      setSelectedBlockId((prev) => prev || blocks[0]?.id || null);
      return undefined;
    }

    let cancelled = false;

    const loadArticle = async () => {
      setLoading(true);
      setError("");

      try {
        const article = await getArticle(routeArticleId);
        const blockResponse = await getArticleBlocks(routeArticleId);
        const blockResult = normalizePaginated(blockResponse);
        const sourceBlocks = blockResult.items.length ? blockResult.items : article.blocks || [];
        const nextBlocks = mapApiBlocksUnique(sourceBlocks);

        if (cancelled) return;

        const nextForm = {
          title: article.title || "",
          slug: article.slug || "",
          excerpt: article.excerpt || "",
          content: article.content || "",
          status: article.status || "draft",
          visibility: article.visibility || "public",
          featured_image: getFeaturedMediaUrl(article),
          featured_media_id: getFeaturedMediaId(article) ? Number(getFeaturedMediaId(article)) : "",
          meta_title: article.meta_title || "",
          meta_description: article.meta_description || "",
          meta_keyword: article.meta_keyword || "",
          category_id: getArticleCategoryId(article) || "",
          subcategory_id: getArticleSubcategoryId(article) || "",
        };

        setForm(nextForm);
        setAssignedCategories(assignedArticleCategories(article));
        setBlocks(nextBlocks);
        setSelectedBlockId(nextBlocks[0]?.id || null);
        setSavedSnapshot(buildSnapshot(nextForm, nextBlocks));
      } catch (err) {
        if (!cancelled) resolveApiError(err, setError, navigate);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadArticle();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeArticleId, isRouteEdit]);

  const handleSave = async ({ previewAfter = false } = {}) => {
    if (!form.title.trim()) {
      setError("Title is required.");
      return false;
    }

    setSaving(true);
    setError("");
    setBlockErrors({});
    if (!previewAfter) {
      setNotice("");
    }

    let workingArticleId = articleId;

    try {
      const now = new Date().toISOString();
      const payload = {
        title: form.title,
        slug: form.slug || makeSlug(form.title),
        excerpt: form.excerpt || null,
        content: form.content || null,
        featured_media_id: form.featured_media_id ? Number(form.featured_media_id) : null,
        status: form.status || "draft",
        visibility: form.visibility || "public",
        meta_title: form.meta_title || null,
        meta_description: form.meta_description || null,
        meta_keyword: form.meta_keyword || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        subcategory_id: form.subcategory_id ? Number(form.subcategory_id) : null,
      };

      const wasCreate = !workingArticleId;
      if (wasCreate) {
        payload.created_at = now;
        if (payload.status === "published") payload.published_at = now;
      }

      const savedArticle = workingArticleId
        ? await updateArticle(workingArticleId, payload)
        : await createArticle(payload);

      workingArticleId = savedRecordId(savedArticle) || workingArticleId;
      if (!workingArticleId) {
        throw new Error("Unable to create this article.");
      }

      // Keep the ID even if the blocks request fails, so the next save uses PUT.
      setPersistedArticleId(String(workingArticleId));

      const savedBlocks = await saveArticleBlocks(workingArticleId, blocks);
      const blockItems = Array.isArray(savedBlocks)
        ? savedBlocks
        : normalizePaginated(savedBlocks).items;
      const nextBlocks = mapApiBlocksUnique(blockItems.length ? blockItems : blocks);
      const nextForm = {
        ...form,
        featured_media_id: getFeaturedMediaId(savedArticle)
          ? Number(getFeaturedMediaId(savedArticle))
          : form.featured_media_id,
        featured_image: getFeaturedMediaUrl(savedArticle) || form.featured_image,
      };
      setForm(nextForm);
      setBlocks(nextBlocks);
      setSelectedBlockId((current) => {
        if (!nextBlocks.length) return null;
        if (nextBlocks.some((block) => block.id === current)) return current;
        const previousIndex = blocks.findIndex((block) => block.id === current);
        return nextBlocks[Math.max(0, previousIndex)]?.id || nextBlocks[0].id;
      });
      setSavedSnapshot(buildSnapshot(nextForm, nextBlocks));

      if (previewAfter) {
        setShowPreviewWarning(false);
        navigate(`/admin/articles/${workingArticleId}/preview`);
        return true;
      }

      setNotice(`Article ${wasCreate ? "created" : "updated"} successfully.`);

      if (wasCreate || String(routeArticleId) !== String(workingArticleId)) {
        navigate(`/admin/articles/${workingArticleId}/edit`, { replace: true });
      }

      return true;
    } catch (err) {
      if (handleApiError(err, navigate)) return false;

      const parsedBlockErrors = parseBlockValidationErrors(err?.payload?.errors, blocks);
      if (parsedBlockErrors.length) {
        const nextErrors = {};
        parsedBlockErrors.forEach(({ blockId, message }) => {
          nextErrors[blockId] = message;
        });
        setBlockErrors(nextErrors);
        setSelectedBlockId(parsedBlockErrors[0].blockId);
        setError(getApiErrorMessage(err));
        scrollToBlock(parsedBlockErrors[0].blockId);
        return false;
      }

      resolveApiError(err, setError, navigate);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (isDirty) {
      setShowPreviewWarning(true);
      return;
    }

    navigate(`/admin/articles/${articleId}/preview`);
  };

  const handleSaveAndPreview = () => {
    handleSave({ previewAfter: true });
  };

  return (
    <div className="articles-page articles-page--editor">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}

      <div className="articles-page__heading">
        <div>
          <h2>{isRouteEdit ? "Edit article" : "Create article"}</h2>
          <p>Build content with blocks, taxonomy and SEO settings.</p>
        </div>
        <div className="articles-page__heading-actions">
          <button className="ui-button" type="button" onClick={() => navigate("/admin/articles")}>
            Cancel
          </button>
          {isRouteEdit && (
            <button
              className="ui-button"
              type="button"
              onClick={handlePreview}
            >
              Preview
            </button>
          )}
          <button
            className="ui-button ui-button--primary"
            type="button"
            disabled={saving || loading}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="articles-page__loading">Loading article...</div>
      ) : (
        <div className="gutenberg-editor">
          <main className="gutenberg-editor__main">
            <div className="gutenberg-editor__label">Add block</div>
            <BlockToolbar
              onAddBlock={(type) => {
                const selectedBlockIndex = blocks.findIndex((block) => block.id === selectedBlockId);
                addBlock(type, selectedBlockIndex >= 0 ? selectedBlockIndex : null);
              }}
            />
            <BlockCanvas
              form={form}
              blocks={blocks}
              blockErrors={blockErrors}
              selectedBlockId={selectedBlockId}
              articleId={articleId}
              onSelectBlock={setSelectedBlockId}
              onUpdateForm={updateForm}
              onUpdateBlock={updateBlock}
              onUpdateBlockType={updateBlockType}
              onAddBlock={addBlock}
              onMoveBlock={moveBlock}
              onDuplicateBlock={duplicateBlock}
              onRemoveBlock={removeBlock}
            />
          </main>

          <aside className="gutenberg-editor__sidebar">
            <div className="gutenberg-editor__label">Settings</div>
            <BlockInspector
              form={form}
              categories={taxonomyCategories}
              articleId={articleId}
              onUpdateForm={updateForm}
            />
          </aside>
        </div>
      )}

      {showPreviewWarning && (
        <div className="articles-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="preview-warning-title">
          <button
            type="button"
            className="articles-confirm-modal__backdrop"
            aria-label="Close"
            disabled={saving}
            onClick={() => setShowPreviewWarning(false)}
          />
          <div className="articles-confirm-modal__panel">
            <h3 id="preview-warning-title" className="articles-confirm-modal__title">
              Unsaved changes
            </h3>
            <p className="articles-confirm-modal__message">
              You have unsaved changes. Save the article to preview your latest edits.
            </p>
            <div className="articles-confirm-modal__actions">
              <button
                type="button"
                className="ui-button"
                disabled={saving}
                onClick={() => setShowPreviewWarning(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ui-button ui-button--primary"
                disabled={saving}
                onClick={handleSaveAndPreview}
              >
                {saving ? "Saving..." : "Save and Preview"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleEditor;
